import Text from 'components/shared/Text'
import { useIntl } from 'react-intl'
import Box from 'components/shared/Box'
import Input from 'components/shared/Input'
import { useAppSelector } from 'store/hooks'
import { useErrorMessages } from 'utils/hooks/useErrorMessages'
import { useDispatch } from 'react-redux'
import { useCallback, useEffect, useRef, useState } from 'react'
import VoucherPreview from 'components/shared/VoucherPreview'
import { setVoucherSignature } from 'store/vouchers-slice'
import { VOUCHER_TYPE_PRODUCT } from 'constants/app'
import { useAutoScroll } from 'utils/hooks'
import { VoucherInfo } from 'routes/OrderAndPayment/sections/VoucherDetails/VoucherInfo'

interface PersonalisedVoucherProps {
    index: number
}

// TODO: unify this component with the one in selfcare's 'order and payment' step

const PersonalisedVoucher = ({ index }: PersonalisedVoucherProps) => {
    const dispatch = useDispatch()
    const textareaRef = useRef(null)
    const { formatMessage } = useIntl()
    const { getError, handleRemoveError } = useErrorMessages()
    const { animatePendingScroll } = useAutoScroll()

    const { vouchersBoxes, vouchersType } = useAppSelector(
        (state) => state.vouchers
    )
    const {
        amount,
        unitPrice,
        voucherSignature,
        cardDetail,
        personDetail,
        voucherType,
        productId,
    } = vouchersBoxes[index]

    const isVoucherProduct = voucherType === VOUCHER_TYPE_PRODUCT
    const componentId = `voucher_preview-${index}`
    const componentErrorId = isVoucherProduct
        ? `itemsBundle[${index}].detail.voucherSignature`
        : `items[${index}].detail.voucherSignature`

    // TODO: relocate following signature logic into hook maybe

    // Raw signature
    const [value, setValue] = useState('')
    // Formated signature visible on preview
    const [formatedSignature, setFormatedSignature] = useState('')

    const handleChange = useCallback(
        (text: string) => {
            const lines = text.split('\n')

            let formattedText = ''

            // Loop through text lines,
            // crop every line if has more then 18 characters and
            // move text to next line
            for (const line of lines) {
                for (
                    let charIndex = 0;
                    charIndex < line.length;
                    charIndex += 18
                ) {
                    formattedText += `${line.slice(
                        charIndex,
                        charIndex + 18
                    )}\n`
                }
            }

            // remove last newline if not intentionally written
            if (lines[lines.length - 1] !== '')
                formattedText = formattedText.replace(/(\n)$/, '')

            // Cropt text if has more thane 3 lines
            formattedText = formattedText.split('\n').slice(0, 3).join('\n')

            setValue(text)
            setFormatedSignature(formattedText)
            dispatch(
                setVoucherSignature({
                    index,
                    signature:
                        formattedText === '' ? [] : formattedText.split('\n'),
                })
            )
        },
        [value, index]
    )

    useEffect(() => {
        if ((amount || productId) && getError(componentErrorId)) {
            handleRemoveError(componentErrorId)
        }
    }, [amount, productId])

    useEffect(() => {
        if (voucherSignature) {
            setValue(voucherSignature.join('\n'))
            setFormatedSignature(voucherSignature.join('\n'))
        }
        animatePendingScroll(componentId)
    }, [])

    return (
        <Box id={componentId}>
            <Text variant="heading3" mb={[16, 24]} mt={[36, 40]}>
                {formatMessage({
                    id: 'selfcare.payment.personalize_voucher',
                    defaultMessage: 'Spersonalizuj voucher',
                })}
            </Text>
            <VoucherInfo />
            <Box isFullWidth direction={['column', 'row']} gap={[26, 16]}>
                <Box isFullWidth minWidth={[0, 310]}>
                    <Input
                        id={componentErrorId}
                        type="textarea"
                        label={formatMessage({
                            id: 'selfcare.payment.personalize_voucher_label',
                            defaultMessage: 'Tekst na voucherze',
                        })}
                        infoMessage={formatMessage({
                            id: 'selfcare.payment.personalize_voucher_max_char',
                            defaultMessage:
                                'Maksymalnie 3 wiersze po 18 znaków',
                        })}
                        isRequired
                        onValueChange={(value) => {
                            handleRemoveError(componentErrorId)
                            handleChange(value)
                        }}
                        isError={!!getError(componentErrorId)}
                        errorMessage={getError(componentErrorId)}
                        onBlurChange={(value) => {
                            handleRemoveError(componentErrorId)
                            handleChange(value)
                        }}
                        value={value}
                        ref={textareaRef}
                        minHeight={134}
                        testId={`voucher-${index}-field-signature`}
                    />
                </Box>
                <VoucherPreview
                    voucherSignature={formatedSignature}
                    unitPrice={(amount ?? 0) * 100 || unitPrice || 0}
                    voucherType={vouchersType || undefined}
                    voucherDetails={cardDetail}
                    personDetail={personDetail}
                />
            </Box>
        </Box>
    )
}

export default PersonalisedVoucher
